import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { ColumnDef } from '@3ddv/software-division-components/backoffice/table';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import {
  AppDialogService,
  DialogCloseReason,
} from '../layout/dialog/app-dialog.service';
import { DiscountDeleteComponent } from './modals/delete/delete.component';
import { DiscountCreateComponent } from './modals/create/create.component';
import { DiscountEditComponent } from './modals/edit/edit.component';
import { Discount } from './models/discount.model';
import { DiscountsService } from './services/discounts.service';
import { DatePipe } from '@angular/common';
import { PaginationComponent } from '../layout/pagination/pagination.component';
import { GsTableComponent } from '../../shared/ui/table/gs-table.component';
import { AuthStore } from '../../core/stores/auth.store';
import { buildDiscountColumns } from './discounts.columns';

@Component({
  selector: 'app-discounts',
  templateUrl: './discounts.component.html',
  styleUrls: ['./discounts.component.css'],
  imports: [ButtonComponent, GsTableComponent, PaginationComponent],
})
export class DiscountsComponent implements OnInit, OnDestroy {
  private dialogService = inject(AppDialogService);
  protected readonly authStore = inject(AuthStore);
  private discountsService = inject(DiscountsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private datePipe = new DatePipe('en-US');

  public data: Array<Discount>;
  public totalItemsInDb: number;
  public itemsPerPage: number;
  public currentPage: number;
  public refreshing: boolean;
  public subscriptions: Array<Subscription>;
  public columns: ColumnDef<Discount>[] = [];
  @ViewChild('pagination') pagination: any;

  constructor() {
    this.data = [];
    this.subscriptions = [];
    this.refreshing = false;
    this.totalItemsInDb = 0;
    this.itemsPerPage = 0;
    this.currentPage = this.activatedRoute.snapshot.queryParams['page'];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  ngOnInit(): void {
    this.columns = buildDiscountColumns(
      this.datePipe,
      discount => this.toggleEnabled(discount),
      discount => this.openModalEdit(discount),
      discount => this.openModalDelete(discount.id),
      this.authStore.canEdit()
    );

    let resolved = this.activatedRoute.snapshot.data['api'];
    this.data = resolved.data;
    this.totalItemsInDb = resolved.metadata.total;
    this.itemsPerPage = resolved.metadata.perPage;
    this.currentPage = 1;

    const queryparams$ = this.activatedRoute.queryParams.subscribe(params => {
      let resolved = this.activatedRoute.snapshot.data['api'];

      if (!params['page'] || params['page'] > resolved.metadata.lastPage) {
        this.router.navigate(['.'], {
          relativeTo: this.activatedRoute,
          queryParams: { page: 1 },
        });
      }

      if (params['page']) {
        this.currentPage = params['page'];
        this.data = resolved.data;
        this.totalItemsInDb = resolved.metadata.total;
        this.itemsPerPage = resolved.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(
            this.currentPage,
            this.totalItemsInDb,
            this.itemsPerPage
          );
        }
      }
    });
    this.subscriptions.push(queryparams$);
  }

  public openModalDelete(id) {
    const { closed$ } = this.dialogService.open(DiscountDeleteComponent, {
      size: 'md',
      inputs: { id: id },
    });
    this.refreshListOnSuccess(closed$, 1);
  }

  public openModalEdit(item: Discount) {
    const { closed$ } = this.dialogService.open(DiscountEditComponent, {
      size: 'lg',
      inputs: { item: JSON.parse(JSON.stringify(item)) },
    });
    this.refreshListOnSuccess(closed$, this.currentPage);
  }

  public openModalCreate() {
    const { closed$ } = this.dialogService.open(DiscountCreateComponent, {
      size: 'lg',
    });
    this.refreshListOnSuccess(closed$, 1);
  }

  private refreshListOnSuccess(
    closed$: Observable<DialogCloseReason | undefined>,
    page?
  ) {
    const onClose$ = closed$.subscribe(reason => {
      if (reason === 'success') {
        this.refreshing = true;
        this.getall(page);
      }
    });
    this.subscriptions.push(onClose$);
  }

  public toggleEnabled(item: Discount) {
    item.enabled = !item.enabled;
    this.data = [...this.data];
    const update$ = this.discountsService.update$(item).subscribe(() => {});
    this.subscriptions.push(update$);
  }

  private getall(page?) {
    if (!page) page = this.currentPage;

    const all$ = this.discountsService.getAll$(this.currentPage).subscribe({
      next: (res: any) => {
        this.refreshing = false;
        this.data = res.data;
        this.totalItemsInDb = res.metadata.total;
        this.itemsPerPage = res.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(
            this.currentPage,
            this.totalItemsInDb,
            this.itemsPerPage
          );
        }
        if (this.currentPage > res.metadata.lastPage) {
          this.currentPage = page;
        }
        this.router.navigate(['/private/discounts'], {
          queryParams: { page: this.currentPage },
        });
      },
      error: err => {
        console.log(err);
      },
    });
    this.subscriptions.push(all$);
  }

  public pageChanged($event) {
    this.router.navigate(['/private/discounts'], {
      queryParams: { page: $event.page },
    });
  }
}
